import { c as _c } from "react/compiler-runtime";
import type { RunnerAttention } from '@shared/all/domains/electron';
import { type RunnerAttentionInput, runnerAttentionItems, runnerAttentionLevel } from '@shared/all/helpers/runnerAttention';
import { useEffect, useState } from 'react';
import type { BoardAgentAPI } from '../helpers/remoteBoardAgent';
type Options = Omit<RunnerAttentionInput, 'attention'> & {
  /** The picked runner's agent; null while it is offline or unpicked. */
  agent: BoardAgentAPI | null;
  /** `attention` is owner only: a member gets offline and setup state alone. */
  isOwner: boolean;
  /** Changes when the runner's config does (repository, token, setup phase),
   *  so a fix made in Runner Settings clears its item. */
  refreshKey: string;
};

/**
 * The runner badge's list, for web and mobile alike. Asks the runner once per
 * agent (a reconnect is a new agent), on each config change, and on
 * `refresh()` - the list calls it when it opens, so it never shows a fix the
 * owner already made.
 */
export function useRunnerAttention(t0) {
  const $ = _c(14);
  const {
    agent,
    isOwner,
    refreshKey,
    ...input
  } = t0;
  const [answer, setAnswer] = useState(null);
  const [seq, setSeq] = useState(0);
  const asks = agent !== null && isOwner;
  let t1;
  if ($[0] !== agent || $[1] !== isOwner) {
    t1 = () => {
      if (!agent || !isOwner) {
        return;
      }
      let ignore = false;
      Promise.resolve().then(() => agent.attention()).then(value => {
        if (!ignore) {
          setAnswer({
            agent,
            value
          });
        }
      }).catch(() => {
        if (!ignore) {
          setAnswer({
            agent,
            value: null
          });
        }
      });
      return () => {
        ignore = true;
      };
    };
    $[0] = agent;
    $[1] = isOwner;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== agent || $[4] !== isOwner || $[5] !== refreshKey || $[6] !== seq) {
    t2 = [agent, isOwner, refreshKey, seq];
    $[3] = agent;
    $[4] = isOwner;
    $[5] = refreshKey;
    $[6] = seq;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  useEffect(t1, t2);
  const attention = asks && answer?.agent === agent ? answer.value : null;
  const items = runnerAttentionItems({
    ...input,
    attention
  });
  const t3 = runnerAttentionLevel(items);
  let t4;
  if ($[8] !== setSeq) {
    t4 = () => setSeq(_temp);
    $[8] = setSeq;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== items || $[11] !== t3 || $[12] !== t4) {
    t5 = {
      items,
      level: t3,
      refresh: t4
    };
    $[10] = items;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}
function _temp(n) {
  return n + 1;
}